import React, { useEffect, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { scrollToSectionById } from "../../utils/sectionScroll";

const SLIDE_MS = 7000;

const slides = [
  {
    id: "platform",
    tab: "AmpTrans",
    kicker: "The platform",
    tagline: "Plan the future of electrification.",
    body: "The platform for making smarter decisions about fleets, charging infrastructure, and the electric grid.",
    cta: "Explore the platform",
    to: "/#solutions",
    image: "/img/homepage/generic_bg.jpeg",
    imageAlt: "Placeholder — platform visual",
    accent: "blue",
  },
  {
    id: "efleetplan",
    tab: "eFleetPlan",
    kicker: "For fleets",
    tagline: "Turn fleet electrification into a plan.",
    body: "Model your fleet, understand your energy needs, and build an actionable roadmap to electric.",
    cta: "See eFleetPlan",
    to: "/solutions/fleet",
    image: "/img/homepage/busfleet.jpeg",
    imageAlt: "Placeholder — eFleetPlan product visual",
    accent: "blue",
  },
  {
    id: "chargerplan",
    tab: "ChargerPlan",
    kicker: "For charging",
    tagline: "Find the right place to charge.",
    body: "Discover and evaluate sites for EV charging with the data you need to move from opportunity to deployment.",
    cta: "See ChargerPlan",
    to: "/solutions/charging",
    image: "/img/homepage/socalsites.jpeg",
    imageAlt: "Placeholder — ChargerPlan product visual",
    accent: "teal",
  },
];

const Arrow = ({ dir }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {dir === "prev" ? <path d="m15 18-6-6 6-6" /> : <path d="m9 18 6-6-6-6" />}
  </svg>
);

export const LandingSlideshow = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((next) => {
    setIndex((i) => (next + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setTimeout(() => go(index + 1), SLIDE_MS);
    return () => window.clearTimeout(timer);
  }, [index, paused, go]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "ArrowRight") go(index + 1);
      if (event.key === "ArrowLeft") go(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, go]);

  return (
    <section
      id="hero"
      className="relative scroll-mt-24 overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/40 to-teal-50/30 lg:-ml-24 lg:pl-24"
      aria-roledescription="carousel"
      aria-label="AmpTrans products"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <style>{`
        @keyframes amp-slide-progress {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
      `}</style>
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #1e3a5f 1px, transparent 0)", backgroundSize: "32px 32px" }} />

      <div className="relative overflow-hidden">
        <div
          className="flex transition-transform duration-700 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {slides.map((slide, i) => (
            <article
              key={slide.id}
              className="w-full min-w-full max-w-full shrink-0 grow-0"
              aria-hidden={i !== index}
              aria-label={`${i + 1} of ${slides.length}: ${slide.tab}`}
            >
              <div className="mx-auto grid max-w-7xl items-center gap-6 px-4 py-8 sm:px-6 md:py-12 lg:grid-cols-2 lg:gap-10 lg:px-8 lg:py-14 2xl:max-w-[1500px]">
                <div className="max-w-xl">
                  <p className={`mb-2 text-xs font-semibold uppercase tracking-wider sm:text-sm ${slide.accent === "teal" ? "text-teal-700" : "text-blue-700"}`}>
                    {slide.kicker}
                  </p>
                  <h1 className="text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-[2.5rem]">
                    {slide.tagline}
                  </h1>
                  <p className="mt-3 text-base leading-relaxed text-slate-600 sm:text-lg">
                    {slide.body}
                  </p>
                  {slide.to.startsWith("/#") ? (
                    <button
                      type="button"
                      onClick={() => scrollToSectionById(slide.to.slice(2), { duration: 1100 })}
                      className="mt-5 inline-flex items-center rounded-lg bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-800"
                    >
                      {slide.cta}
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ml-2">
                        <path d="M5 12h14" />
                        <path d="m12 5 7 7-7 7" />
                      </svg>
                    </button>
                  ) : (
                    <Link
                      to={slide.to}
                      className="mt-5 inline-flex items-center rounded-lg bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-800"
                    >
                      {slide.cta}
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ml-2">
                        <path d="M5 12h14" />
                        <path d="m12 5 7 7-7 7" />
                      </svg>
                    </Link>
                  )}
                </div>

                <div className="relative">
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                    <div className="relative aspect-[16/10] max-h-56 bg-slate-100 sm:max-h-none sm:aspect-[4/3]">
                      <img src={slide.image} alt={slide.imageAlt} className="h-full w-full object-cover" />
                    </div>
                    {/* <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2">
                      <span className="text-sm font-semibold text-slate-800">{slide.tab}</span>
                    </div> */}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <div className="relative border-t border-slate-200/80 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-stretch 2xl:max-w-[1500px]">
          <button
            type="button"
            onClick={() => go(index - 1)}
            className="flex shrink-0 items-center justify-center px-2 text-slate-500 hover:bg-slate-50 hover:text-slate-900 sm:px-4"
            aria-label="Previous slide"
          >
            <Arrow dir="prev" />
          </button>
          <div className="grid min-w-0 flex-1 grid-cols-3" role="tablist" aria-label="Products">
            {slides.map((slide, i) => {
              const active = i === index;
              return (
                <button
                  key={slide.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => go(i)}
                  className={`relative px-2 py-3 text-left transition sm:px-3 sm:py-3.5 ${
                    active ? "bg-white" : "hover:bg-slate-50"
                  }`}
                >
                  <span className={`block truncate text-xs font-semibold sm:text-sm ${active ? "text-slate-900" : "text-slate-500"}`}>
                    {slide.tab}
                  </span>
                  <span className="mt-0.5 hidden truncate text-xs text-slate-400 sm:block">{slide.kicker}</span>
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-slate-200">
                    {active && (
                      <span
                        key={index}
                        className={`block h-full origin-left ${slide.accent === "teal" ? "bg-teal-600" : "bg-blue-700"}`}
                        style={{
                          animation: `amp-slide-progress ${SLIDE_MS}ms linear forwards`,
                          animationPlayState: paused ? "paused" : "running",
                        }}
                      />
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => go(index + 1)}
            className="flex shrink-0 items-center justify-center px-2 text-slate-500 hover:bg-slate-50 hover:text-slate-900 sm:px-4"
            aria-label="Next slide"
          >
            <Arrow dir="next" />
          </button>
        </div>
      </div>
    </section>
  );
};
